import {css, StyleSheet} from 'aphrodite';
import React, {lazy, Suspense} from 'react';

import SelectorOptionInput from 'metadata/SelectorOptionInput';
import {FieldType, isNumericField, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

import {
  FilterLookupStepInfo,
  FilterLookupStepSpec,
  FilterType,
  FILTER_TYPE_TO_READABLE,
  isStoredWithinFilterStep,
  LookupStepType,
  SchemaFieldFilterLookupStepInfo,
  STORED_WITHIN_FILTER_OPTION_LABEL,
} from './LookupConfigModalConstants';
import {filterOptionsForFieldType, SUPPORTED_FILTER_TYPES} from './LookupConfigUtils';
import {getStoredWithinFilterLookupStepInfo} from './LookupStepInitializerUtils';

// Loaded lazily so the storable-search subtree (samples/StorableSearchInput -> models/* ->
// connect/DataConnector) is split into an async chunk.
const StoredWithinFilterValueInput = lazy(
  () => import(/* webpackChunkName: "stored-within-filter-value-input" */ './StoredWithinFilterValueInput')
);

export interface FilterLookupStepFormErrors {
  schemaField: boolean;
  filterValue: boolean;
  storedWithinValue: boolean;
}

// Sentinel value used to represent the "Stored within" inventory filter option in the field picker menu.
const STORED_WITHIN_OPTION_VALUE = '__STORED_WITHIN__';

interface FilterValueFieldProps {
  disabled?: boolean;
  lookupStepInfo: SchemaFieldFilterLookupStepInfo;
  onChange: (lookupInfo: SchemaFieldFilterLookupStepInfo) => void;
}

function FilterValueField(props: FilterValueFieldProps): JSX.Element | null {
  function filterValueChange(value: string | number): void {
    props.onChange({
      ...props.lookupStepInfo,
      lookupStep: {
        ...props.lookupStepInfo.lookupStep,
        value,
      },
    });
  }

  if ([FilterType.ISNULL, FilterType.NOTNULL].includes(props.lookupStepInfo.lookupStep.filterType)) {
    return null;
  }
  const schemaField = props.lookupStepInfo.schemaField;
  if (schemaField && isNumericField(schemaField)) {
    return (
      <NumericInput
        isDisabled={props.disabled}
        placeholder="Enter value"
        value={props.lookupStepInfo.lookupStep.value}
        onChange={(e: NumericInputChangeEvent) => {
          if (e.target.numericValue != null) {
            filterValueChange(e.target.numericValue);
          }
        }}
      />
    );
  }
  if (schemaField && schemaField.fieldType === FieldType.SELECTOR && schemaField.schemaFieldSelectorId) {
    return (
      <SelectorOptionInput
        onChange={(tag) => {
          filterValueChange(tag.value);
        }}
        schemaFieldSelectorId={schemaField.schemaFieldSelectorId}
        shouldAllowMultiple={false}
        defaultValue={{
          schemaFieldSelectorOptionIds: [],
          value: props.lookupStepInfo.lookupStep.value?.toString(),
        }}
      />
    );
  }
  return (
    <TextInput
      disabled={props.disabled}
      value={props.lookupStepInfo.lookupStep.value}
      placeholder="Enter value"
      onChange={(e: InputChangeEvent) => {
        filterValueChange(e.target.value);
      }}
    />
  );
}

export function convertValueToType(
  value: string | number | undefined,
  fieldType: FieldType,
  filterType: FilterType
): string | number | undefined {
  // ISNULL or NOTNULL filters should not have values, so value should be undefined. If not, allow filter to error.
  if (filterType === FilterType.ISNULL || filterType === FilterType.NOTNULL) {
    return value;
  }
  if (value == null) {
    return '';
  }
  if (fieldType === FieldType.INTEGER) {
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? '' : parsed;
    }
    return Math.round(value);
  }
  if (fieldType === FieldType.FLOAT) {
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? '' : parsed;
    }
    return value;
  }
  return String(value);
}

interface FilterLookupStepFormProps {
  lookupStepInfo: FilterLookupStepInfo;
  onChange: (lookupInfo: FilterLookupStepInfo) => void;
  onRemove: () => void;
  disabled?: boolean;
  errors: FilterLookupStepFormErrors;
  fieldOptions: Array<SchemaField>;
  // May be null when the preceding step has no resolved schema (e.g. "Any container"). In that case
  // there are no schema-field options, so only the "Stored within" inventory filter is selectable.
  parentSchema: Schema | null;
  // Whether to surface the "Inventory" section ("Stored within") in the field picker.
  showInventoryFilters?: boolean;
}

function getFieldPickerOptionDatas(
  fieldOptions: Array<SchemaField>,
  showInventoryFilters: boolean
): Array<OptionData> {
  const schemaFieldOptions: Array<OptionData> = fieldOptions.map((option) => ({
    name: option.displayName,
    value: option,
  }));
  // Without inventory filters there is only one kind of option, so no section headers are needed.
  if (!showInventoryFilters) {
    return schemaFieldOptions;
  }
  const optionDatas: Array<OptionData> = [];
  if (schemaFieldOptions.length > 0) {
    optionDatas.push({name: 'Schema fields', isHeader: true}, ...schemaFieldOptions);
  }
  optionDatas.push(
    {name: 'Inventory', isHeader: true},
    {
      name: STORED_WITHIN_FILTER_OPTION_LABEL,
      value: STORED_WITHIN_OPTION_VALUE,
      dataTestAttrs: {component: 'FilterLookupStepForm', element: 'storedWithinOption'},
    }
  );
  return optionDatas;
}

function FieldPicker(props: FilterLookupStepFormProps): JSX.Element {
  const lookupStepInfo = props.lookupStepInfo;
  const schemaField = isStoredWithinFilterStep(lookupStepInfo) ? undefined : lookupStepInfo.schemaField;
  const displayName = isStoredWithinFilterStep(lookupStepInfo)
    ? STORED_WITHIN_FILTER_OPTION_LABEL
    : schemaField
      ? schemaField.displayName
      : 'Missing value';
  return (
    <SelectDropdown
      disabled={props.disabled}
      displayName={displayName}
      alwaysDropDown={true}
      error={!isStoredWithinFilterStep(lookupStepInfo) && props.errors.schemaField ? 'Field deleted' : null}
      menu={
        <SearchableMenu
          selectedValue={isStoredWithinFilterStep(lookupStepInfo) ? STORED_WITHIN_OPTION_VALUE : schemaField}
          onSelect={(value: SchemaField | string) => {
            if (value === STORED_WITHIN_OPTION_VALUE) {
              props.onChange(getStoredWithinFilterLookupStepInfo(props.parentSchema));
              return;
            }
            const option = value as SchemaField;
            const currFilterType = isStoredWithinFilterStep(lookupStepInfo)
              ? FilterType.EQ
              : lookupStepInfo.lookupStep.filterType;
            const currValue = isStoredWithinFilterStep(lookupStepInfo) ? '' : lookupStepInfo.lookupStep.value;
            const newFilterTypes = filterOptionsForFieldType(option.fieldType);
            const newFilterType = newFilterTypes.includes(currFilterType)
              ? currFilterType
              : newFilterTypes[0];
            const newLookupStep: FilterLookupStepSpec = {
              type: LookupStepType.FILTER,
              schemaField: option.apiId,
              filterType: newFilterType,
            };
            if (newFilterTypes.includes(FilterType.EQ)) {
              newLookupStep.value = convertValueToType(currValue, option.fieldType, currFilterType);
            }
            props.onChange({
              name: `Filter: ${option.displayName}`,
              lookupStep: newLookupStep,
              schemaField: option,
              parentSchema: props.parentSchema,
              linkSchema: null,
            });
          }}
          optionDatas={getFieldPickerOptionDatas(props.fieldOptions, props.showInventoryFilters ?? false)}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          scrollable={true}
        />
      }
    />
  );
}

function OperatorPicker(props: FilterLookupStepFormProps): JSX.Element {
  if (isStoredWithinFilterStep(props.lookupStepInfo)) {
    // The "stored within" filter only supports the "is" operator; render it locked.
    return (
      <SelectDropdown
        disabled={true}
        alwaysDropDown={true}
        displayName={FILTER_TYPE_TO_READABLE[FilterType.EQ]}
        menu={<Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.SMALL} />}
      />
    );
  }
  const lookupStepInfo = props.lookupStepInfo;
  const schemaField = lookupStepInfo.schemaField;
  return (
    <SelectDropdown
      disabled={props.disabled}
      alwaysDropDown={true}
      displayName={FILTER_TYPE_TO_READABLE[lookupStepInfo.lookupStep.filterType]}
      menu={
        <Menu
          value={lookupStepInfo.lookupStep.filterType}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.SMALL}
          scrollable={true}
        >
          {(schemaField ? filterOptionsForFieldType(schemaField.fieldType) : SUPPORTED_FILTER_TYPES).map(
            (filterType: FilterType, i) => (
              <Option
                key={i}
                value={filterType}
                onSelect={(filterType: FilterType) => {
                  const currLookupStep = lookupStepInfo.lookupStep;
                  const newLookupStep: FilterLookupStepSpec = [
                    FilterType.ISNULL,
                    FilterType.NOTNULL,
                  ].includes(filterType)
                    ? {
                        // Be sure not to include "value" when switching to isnull/notnull
                        type: LookupStepType.FILTER,
                        schemaField: currLookupStep.schemaField,
                        filterType,
                      }
                    : {
                        ...currLookupStep,
                        filterType,
                        value: currLookupStep.value ?? '',
                      };
                  props.onChange({
                    ...lookupStepInfo,
                    lookupStep: newLookupStep,
                  });
                }}
              >
                {FILTER_TYPE_TO_READABLE[filterType]}
              </Option>
            )
          )}
        </Menu>
      }
    />
  );
}

function ValueField(props: FilterLookupStepFormProps): JSX.Element | null {
  if (isStoredWithinFilterStep(props.lookupStepInfo)) {
    const lookupStepInfo = props.lookupStepInfo;
    return (
      <>
        <Suspense
          fallback={
            <TextInput
              disabled={props.disabled}
              placeholder="Loading..."
              readOnly={true}
              value={lookupStepInfo.lookupStep.value}
            />
          }
        >
          <StoredWithinFilterValueInput
            disabled={props.disabled}
            lookupStepInfo={lookupStepInfo}
            onChange={props.onChange}
          />
        </Suspense>
        {props.errors.storedWithinValue ? (
          <ErrorMessage error="Select an inventory item to filter by" />
        ) : null}
      </>
    );
  }
  const lookupStepInfo = props.lookupStepInfo;
  return (
    <>
      <FilterValueField disabled={props.disabled} lookupStepInfo={lookupStepInfo} onChange={props.onChange} />
      {props.errors.filterValue ? (
        <ErrorMessage
          error={`Filter value must be a number for ${lookupStepInfo.schemaField!.displayName}`}
        />
      ) : null}
    </>
  );
}

export default function FilterLookupStepForm(props: FilterLookupStepFormProps): JSX.Element {
  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'FilterLookupStepForm', element: 'root'})}
    >
      <span className={css(styles.content)}>
        <div className={css(styles.innerWrapper)}>
          <Row>
            <Col md={6} className={css(styles.field)}>
              <FieldPicker {...props} />
            </Col>
            <Col md={6} className={css(styles.field)}>
              <OperatorPicker {...props} />
            </Col>
          </Row>
          <Row>
            <Col md={12} className={css(styles.field)}>
              <ValueField {...props} />
            </Col>
          </Row>
        </div>
      </span>
      <span>
        <BenchlingIcon className={css(styles.removeIcon)} icon="close" onClick={props.onRemove} />
      </span>
    </div>
  );
}

const styles = StyleSheet.create({
  field: {
    ...FONT_PROXIMA_13,
    marginBottom: '8px',
  },
  wrapper: {
    display: 'flex',
  },
  content: {
    flexGrow: 1,
    margin: -10,
    overflowX: 'hidden',
  },
  innerWrapper: {
    padding: 10,
  },
  removeIcon: {
    color: GRAY_DARK,
    cursor: 'pointer',
    fontSize: 10,
    margin: '9px 2px 9px 9px',
  },
});
